import React, { useCallback, useContext } from "react";
import { useSelector } from "react-redux";

import useAuth from "../hooks/useAuth";

import { useEffect } from "react";
import appContext from "../app/contextState/context";
import { frequency } from "../config/constants";
import useChat from "../hooks/useChat";
import useAlert from "../hooks/useAlert";
import Route from "./Route";
import { keys } from "../config/keys";
import {
  STANDALONE_FEATURES,
  buildStandaloneUrl,
  canAccessStandaloneFeature,
} from "../utils/standaloneFeatures";

const academyAuthQuery = () => {
  const token = localStorage.getItem("token") ?? "";
  const sessionKey = localStorage.getItem("session_key") ?? "";
  return `token=${encodeURIComponent(token)}&session_key=${encodeURIComponent(
    sessionKey
  )}`;
};

const Sidebar = () => {
             const { user } = useSelector((state) => state.auth);
  const { showAlert } = useAlert();
  const {
    permissions,
    onLogout,
    isAdmin,
    isCompany,
    isGuest,
    isManager,
    isSupervisor,
    isCompanyAdmin,
    isFellowAdmin
  } = useAuth();
  const { fetchChats } = useChat();
  const appState = useContext(appContext);
  const { unseen } = appState;

  const openAcademy = useCallback(async () => {
    const role = user?.role;
    if (role !== "company" && role !== "employee") {
      window.open(
        `https://academy.compcheck.io/?${academyAuthQuery()}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    const userID =
      role === "company"
        ? user?._id
        : user?.companyID?._id ?? user?.companyID;
    if (!userID) {
      showAlert("Unable to open Academy for this account.", "danger");
      return;
    }
    try {
      const url = `${keys.SUBDOMAIN_SERVICE_URL}/getCompanySubdomain?userID=${encodeURIComponent(
        userID
      )}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data?.status || !data?.result?.name) {
        showAlert(data?.message || "Could not load Academy link.", "danger");
        return;
      }
      window.open(
        `https://${data.result.name}.compcheck.io/?${academyAuthQuery()}`,
        "_blank",
        "noopener,noreferrer"
      );
    } catch {
      showAlert("Failed to reach Academy. Try again later.", "danger");
    }
  }, [user, showAlert]);

  const standaloneReturnUrl = `${keys.CLIENT_ORIGIN || window.location.origin}/dashboard`;
  const standaloneAuthFlags = { isAdmin, isCompanyAdmin, isFellowAdmin, isManager };

  const adminStandaloneSubRoutes = [
    canAccessStandaloneFeature("COMPANIES", standaloneAuthFlags) && {
      title: "Registered Companies",
      href: buildStandaloneUrl(STANDALONE_FEATURES.COMPANIES.path, {
        returnUrl: standaloneReturnUrl,
        admin: true,
      }),
    },
    {
      title: "Categories",
      to: "/categories?admin=true",
    },
    {
      title: "Industries",
      to: "/natures?admin=true",
    },
    !isCompanyAdmin &&
      !isFellowAdmin && {
        title: "Super Admins",
        to: "/admins?admin=true",
      },
    canAccessStandaloneFeature("COMPANY_ADMINS", standaloneAuthFlags) && {
      title: "Admins",
      href: buildStandaloneUrl(STANDALONE_FEATURES.COMPANY_ADMINS.path, {
        returnUrl: standaloneReturnUrl,
        admin: true,
      }),
    },
    canAccessStandaloneFeature("FELLOW_ADMINS", standaloneAuthFlags) && {
      title: "Fellow Admins",
      href: buildStandaloneUrl(STANDALONE_FEATURES.FELLOW_ADMINS.path, {
        returnUrl: standaloneReturnUrl,
        admin: true,
      }),
    },
    isCompanyAdmin && {
      title: "Subscription",
      to: "/subscription",
    },
    {
      title: "System Alerts",
      to: "/system-alerts",
    },
  ].filter((item) => item);

  // ----------------------NAVIGATION----------------------
  const baseRoutes = [
    {
      title: "Dashboard",
      to: "/",
      icon: <i className="bi bi-grid"></i>,
    },
    isGuest
      ? null
      : {
        title: "Academy",
        navHandler: openAcademy,
        icon: <i className="bi bi-person-workspace"></i>,
      },
    isGuest
      ? null
      : {
        title: "HR",
        href: `${keys.HR_ORIGIN}/?token=${localStorage.getItem('token')}&session_key=${localStorage.getItem('session_key')}`,
        icon: <i className="bi bi-person-workspace"></i>,
      },
    ((!isManager) &&
      (!isCompany) &&
      !isAdmin) ||
      isGuest
      ? null
      : {
        title: "Checklists",
        icon: <i className="bi bi-card-checklist"></i>,
        id: "checklist",
        subRoutes: [
          {
            title: "Checklist Collection",
            to: "/checklists",
          },
          isAdmin && {
            title: "Reviews",
            to: "/checklists?review=true",
          },
          (isCompany || isAdmin || isManager) && {
            title: "Add New Checklist",
            to: "/new-checklist",
          },
          (isCompany || isAdmin || isManager) && {
            title: "Favourites",
            to: "/checklists?favourite=true",
          },
        ].filter((item) => item),
      },
    ((!isManager) &&
      (!isCompany) &&
      !isAdmin) ||
      isGuest
      ? null
      : {
        title: "Risk Assessments",
        icon: <i className="bi bi-card-checklist"></i>,
        id: "riskAssesment",
        subRoutes: [
          {
            title: "RA Collection",
            to: "/risk-assesments",
          },
          isAdmin && {
            title: "Reviews",
            to: "/risk-assesments?review=true",
          },
          (isCompany || isAdmin || isManager) && {
            title: "Add New RA",
            to: "/new-risk-assesment",
          },
        ].filter((item) => item),
      },
    ((!isManager) &&
      (!isCompany) &&
      !isAdmin) ||
      isGuest
      ? null
      : {
        title: "Audits",
        icon: <i className="bi bi-card-checklist"></i>,
        id: "audit",
        subRoutes: [
          {
            title: "Audit Collection",
            to: "/audits",
          },
          isAdmin && {
            title: "Reviews",
            to: "/audits?review=true",
          },
          (isCompany || isAdmin || isManager) && {
            title: "Add New Audit",
            to: "/new-audit",
          },
        ].filter((item) => item),
      },
    (!isManager) &&
      (!isCompany) &&
      !isAdmin
      ? null
      : (isCompany || isAdmin || isManager) && {
        title: "Policies",
        icon: <i className="bi bi-card-checklist"></i>,
        id: "policy",
        subRoutes: [
          {
            title: "All Policies",
            to: "/policy",
          },
          (isCompany || isAdmin) && {
            title: "Add New Policy",
            to: "/policies-options",
          },
        ].filter((item) => item),
      },
    ((!isManager) &&
      (!isCompany) &&
      !isAdmin)
      ? null
      : {
        title: "Forms",
        icon: <i className="bi bi-person"></i>,
        id: "form",
        subRoutes: [
          {
            title: "Collection",
            to: "/form-list",
          },
          {
            title: "Add Form",
            to: "/add-form",
          },
          {
            title: "Submissions",
            to: "/submission-list",
          },
        ],
      },
    ((!isManager) &&
      (!isCompany) &&
      !isAdmin)
      ? null
      : {
        title: "Agreements",
        icon: <i className="bi bi-card-checklist"></i>,
        id: "agreement",
        subRoutes: [
          {
            title: "All Agreements",
            to: "/agreement",
          },
          (isCompany || isAdmin) && {
            title: "Add New Agreement",
            to: "/agreement-options",
          },
          {
            title: "Submissions",
            to: "/agreement-submissions",
          },
        ].filter((item) => item),
      }
  ].filter((item) => item);

  const companyRoutes = [
    {
      title: "Records",
      to: "/records",
      id: "record",
      icon: <i className="bi bi-clipboard-data"></i>,
    },
    ...(isGuest
      ? []
      : [
        {
          title: "Tickets",
          to: "/tickets",
          id: "tickets",
          icon: <i className="bi bi-files"></i>,
        },
        {
          title: "Tasks",
          icon: <i className="bi bi-card-checklist"></i>,
          id: "task",
          subRoutes: ["All", ...frequency, "None"].map((item) => ({
            title: item,
            to: `/tasks?frequency=${item}`,
          })),
        },
      ]),
    ...(isSupervisor ||
      isGuest
      ? []
      : [

        {
          title: "Document Library",
          to: "/documents",
          id: "document",
          icon: <i className="bi bi-files"></i>,
        },
      ]),

    ...(isSupervisor || isGuest || isManager
      ? []
      : [
        {
          title: "Public Library",
          to: "/documents?public=true",
          id: "public",
          icon: <i className="bi bi-globe"></i>,
        },
      ]),
    ...(isSupervisor || isGuest || isManager
      ? [] :
      [

        {
          title: "Management System",
          to: "/management-system",
          id: "public",
          icon: <i className="bi bi-file-earmark"></i>,
        },
      ]),
  ].filter((item) =>
    isAdmin
      ? true
      : item.id == "record" && isGuest
        ? true
        : true
  );

  const adminRoutes = [
    (isAdmin || isCompany || isCompanyAdmin || isFellowAdmin) && {
      title: "Admin",
      icon: <i className="bi bi-person"></i>,
      id: "admin",
      subRoutes: isAdmin
        ? adminStandaloneSubRoutes
        : isCompany
          ? [
            {
              title: "Sites",
              to: "/branches?admin=true",
            },
            {
              title: "Subscription",
              to: "/subscription",
            },
            {
              title: "Payment Methods",
              to: "/payment-method",
            }
          ]
          : isManager
            ? []
            : null,
    },
    isAdmin && {
      title: "CRM",
      icon: <i className="bi bi-person"></i>,
      id: "crm",
      subRoutes: isAdmin
        ? [
          {
            title: "Accounts",
            to: "/crm-collection",
          },
          {
            title: "Contacts",
            to: "/contacts",
          },
          {
            title: "CRM Tasks",
            to: "/crm-to-do",
          },
        ].filter(it => it)
        : null,
    },
    (isAdmin || isFellowAdmin) && {
      title: "Subscriptions",
      icon: <i className="bi bi-person"></i>,
      id: "subscription",
      subRoutes: isAdmin && !isCompanyAdmin
        ? [
          {
            title: "Create Subscription",
            to: "/subscription-options",
          },
          {
            title: "List",
            to: "/subscription-list",
          },
        ].filter(it => it)
        : isCompanyAdmin
          ? [
            {
              title: "Create Subscription",
              to: "/subscription-options",
            },
            {
              title: "List",
              to: "/subscription-list",
            },
            {
              title: "Payment Methods",
              to: "/payment-method",
            },
            {
              title: "Stripe Connect",
              to: "/connect-stripe",
            },
          ].filter(it => it)
          : null,
    },
    isAdmin && {
      title: "Proposals",
      icon: <i className="bi bi-person"></i>,
      id: "proposal",
      subRoutes: isAdmin
        ? [
          {
            title: "Create Proposal",
            to: "/add-proposal",
          },
          {
            title: "List",
            to: "/list-Proposal",
          },
        ].filter(it => it)
        : null,
    },
    isAdmin && {
      title: "Invoices",
      icon: <i className="bi bi-person"></i>,
      id: "invoice",
      subRoutes: isAdmin
        ? [
          {
            title: "Create Invoice",
            to: "/add-invoice",
          },
          {
            title: "List",
            to: "/list-Invoice",
          },
        ].filter(it => it)
        : null,
    },
    isAdmin && {
      title: "Promo Codes",
      icon: <i className="bi bi-person"></i>,
      id: "coupon",
      subRoutes: isAdmin
        ? [
          {
            title: "Add Promo Code",
            to: "/add-coupon",
          },
          {
            title: "List",
            to: "/list-coupon",
          },
        ].filter(it => it)
        : null,
    },
  ].filter(it => it);

  const communicationRoutes = [
    !isAdmin
      ? null
      : {
        title: "Broadcast",
        to: "/broadcast",
        id: "broadcast",
        icon: <i className="bi bi-broadcast-pin"></i>,
      },
    {
      title: `Inbox`,
      to: "/inbox",
      id: "inbox",
      unseen: unseen,
      icon: <i className="bi bi-inbox-fill"></i>,
    },
    (isCompany || isAdmin || isManager) && {
      title: "Emails",
      icon: <i className="bi bi-envelope"></i>,
      id: "email",
      subRoutes: [
        {
          title: "Email Database",
          to: "/email",
        },
        {
          title: "Email Confirmations",
          to: "/sentMails",
        },
        {
          title: "Pending Confirmations",
          to: "/sentMails?status=pending",
        },
        {
          title: "Pending Mail",
          to: "/pending-mails",
          // id: "pending-mails",
          // icon: <i className="bi bi-broadcast-pin"></i>,
        }
      ].filter((item) => item),

    },
    {
      title: "FAQ's",
      to: "/faqs",
      id: "faqs",
      icon: <i className="bi bi-question-circle"></i>,
    },
  ].filter((item) => item);
  useEffect(() => {
    if (isAdmin || isCompany) {
      fetchChats();
    }
  }, []);

  const canViewAdminAndCommunication = isAdmin || isCompany || isManager;

  return (
    <aside id="sidebar" className="sidebar">
      <ul className="sidebar-nav" id="sidebar-nav">
        {baseRoutes?.map((route, index) => (
          <Route route={route} key={index} />
        ))}

        <li className="nav-item">
          <a
            className="nav-link"
            data-bs-target="#data-menu"
            data-bs-toggle="collapse"
            href="#"
            aria-expanded="true"
          >
            <i className="bi bi-collection"></i>
            <span>Data</span>
            <i className="bi bi-chevron-down ms-auto"></i>
          </a>
          <ul id="data-menu" className="collapse show">
            {companyRoutes?.map((route, index) => (
              <Route route={route} collapseParentId="data-menu" key={`data-company-${index}`} />
            ))}
            {canViewAdminAndCommunication
              ? adminRoutes?.map((route, index) => (
                <Route route={route} collapseParentId="data-menu" key={`data-admin-${index}`} />
              ))
              : null}
          </ul>
        </li>

        {canViewAdminAndCommunication ? (
          <li className="nav-item">
            <a
              className="nav-link"
              data-bs-target="#communication-menu"
              data-bs-toggle="collapse"
              href="#"
              aria-expanded="true"
            >
              <i className="bi bi-chat-left-text"></i>
              <span>Communication</span>
              <i className="bi bi-chevron-down ms-auto"></i>
            </a>
            <ul
              id="communication-menu"
              className="collapse show"
            >
              {communicationRoutes?.map((route, index) => (
                <Route route={route} collapseParentId="communication-menu" key={`communication-${index}`} />
              ))}
            </ul>
          </li>
        ) : null}

        {/* Logout Button */}
        <li className="nav-heading">
          {user?.role == "admin" ? "Login as Company" : "Login as Admin"}
        </li>
        <li className="nav-item">
          <button
            className="nav-link collapsed border-0 w-100"
            onClick={onLogout}
          >
            <i className="bi bi-box-arrow-in-left"></i>
            <span>Logout</span>
          </button>
        </li>
      </ul>
    </aside>
  );
};

export default Sidebar;
